import api from './api';

/**
 * PRX-128 — the adjudicator directory. Wire shapes of
 * `GET /api/adjudicators/pharmacies/:pharmacyId/lookup?bin=&carrier=` and
 * `POST /api/adjudicators/pharmacies/:pharmacyId/client-id/check`
 * (backend-v2/src/adjudicators/adjudicators.service.ts).
 */
/**
 * Mirrors `ClientIdRuleKind` in backend-v2/src/adjudicators/adjudicator-directory.types.ts
 * (and the shapes below mirror that file and adjudicators.service.ts); change them together.
 */
export type ClientIdRuleKind =
  | 'drop_trailing_01'
  | 'telus_assure'
  | 'manulife_10_digit'
  | 'gsc_certificate'
  | 'ifh_uci'
  | 'append_01'
  | 'pshcp'
  | 'nihb'
  | 'ontario_health_number'
  | 'none';

export interface AdjudicatorGroup {
  number: string;
  label: string | null;
}

export interface AdjudicatorPlan {
  id: string;
  adjudicator: string;
  network: string;
  bin: string;
  carrierIds: string[];
  name: string;
  companyNote: string | null;
  /** Null when the reference sheet gives a placeholder only (`groupNote`). */
  groups: AdjudicatorGroup[] | null;
  groupNote: string | null;
  notes: string;
  clientIdRule: ClientIdRuleKind;
  /** Open questions: the rule is not confirmed, so it is shown, not applied. */
  needsConfirmation: string[];
  /** The last day the plan was in effect. */
  expired?: string;
  sponsorIds: string[];
  discountPlan?: boolean;
}

export interface AdjudicatorSponsor {
  id: string;
  name: string;
  planIds: string[];
  needsConfirmation: string[];
}

/** Set when the backend read a single-digit carrier as its 2-digit form (`2` → `02`). */
export type CarrierNormalised = { from: string; to: string } | null;

export interface AdjudicatorLookupResponse {
  dataVersion: string;
  bin: string;
  /** The carrier of the directory key used (upper case, 2 digits when `carrierNormalised` says so). */
  carrierId: string;
  carrierNormalised: CarrierNormalised;
  /** Empty when the pair is not in the directory. */
  plans: AdjudicatorPlan[];
  sponsors: AdjudicatorSponsor[];
  clientIdRule: ClientIdRuleKind | null;
}

export interface ClientIdWarning {
  code: string;
  severity: 'warning' | 'info';
  message: string;
}

export interface ClientIdCheckResponse {
  dataVersion: string;
  planKey: string;
  carrierNormalised: CarrierNormalised;
  clientIdRule: ClientIdRuleKind;
  /** The client ID under the plan's rule. */
  value: string;
  /** Null: the plan's rule is not confirmed, so the ID is neither right nor wrong. */
  valid: boolean | null;
  /** Every change to the ID, and every note on the plan. */
  warnings: ClientIdWarning[];
}

const base = (pharmacyId: string) => `/api/adjudicators/pharmacies/${encodeURIComponent(pharmacyId)}`;

const adjudicatorsService = {
  async lookup(pharmacyId: string, bin: string, carrier: string): Promise<AdjudicatorLookupResponse> {
    const response = await api.get<AdjudicatorLookupResponse>(`${base(pharmacyId)}/lookup`, {
      params: carrier ? { bin, carrier } : { bin },
    });
    return response.data;
  },

  async checkClientId(
    pharmacyId: string,
    body: { bin: string; carrierId?: string; clientId: string },
  ): Promise<ClientIdCheckResponse> {
    const response = await api.post<ClientIdCheckResponse>(`${base(pharmacyId)}/client-id/check`, body);
    return response.data;
  },
};

export default adjudicatorsService;
